import { useId } from 'react';
import type { ReactNode } from 'react';
import { FEATURE_ICONS } from '@yourstore/theme';
import type { SlotSpec } from '@yourstore/theme';
import { suspectedPolicyKinds } from '@yourstore/domain';
import type { AuthoredPolicy, DerivedPolicy } from '@yourstore/domain';
import { useT } from '../../../messages';
import type { T } from '../../../messages';
import type { ZoneReport } from '../../../wire';

/**
 * **A section's slots as friendly controls** — one control per slot *kind*,
 * generated from the vocabulary's shapes, so a block the builder has never
 * been told about still gets a complete inspector.
 *
 * Ported from the old form editor where it was sound (the policy rows and
 * their badges, the links, the "what survived" report on a content zone),
 * with the list kinds it never had an editor for — `features`, `panels`,
 * `tiers` — drawn by one list editor over a field table rather than three
 * more bespoke components. The rule the old editor ran on still holds: an
 * entry's keys this control does not draw are **kept**, never dropped, so a
 * keystroke in a title cannot delete a field the server would have taken.
 *
 * The labels are the console's words for a slot when it has them
 * (`builder.slotName`), else the vocabulary's own.
 */

export interface SlotProps {
  slot: SlotSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  /** What the sanitizer did to this slot on the last publish, when it is a content zone. */
  zone?: ZoneReport | undefined;
  /** The engine's rules — handed only to the `policies` block's rows. */
  policyRules?: readonly DerivedPolicy[] | undefined;
}

export function slotLabel(t: T, slot: SlotSpec): string {
  const own = t('builder.slotName', { slot: slot.name });
  return own === '' ? slot.label : own;
}

/** The console's word for an option (`left`, `glass`), else the option as written. */
export function optionLabel(t: T, option: string): string {
  const own = t('builder.optionName', { option });
  if (own !== '') return own;
  const spaced = option.replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function Hint({ t, slot }: { t: T; slot: SlotSpec }): ReactNode {
  const own = t('builder.slotHint', { slot: slot.name });
  if (own !== '') return <span className="bld-hint">{own}</span>;
  return null;
}

export function SlotField(props: SlotProps): ReactNode {
  const t = useT();
  const id = useId();
  const { slot, value, onChange } = props;
  const label = slotLabel(t, slot);
  const text = typeof value === 'string' ? value : '';

  switch (slot.kind) {
    case 'text':
    case 'image_url':
      return (
        <div className="bld-field">
          <label htmlFor={id}>{label}</label>
          <input
            id={id}
            className="bld-input"
            type={slot.kind === 'image_url' ? 'url' : 'text'}
            inputMode={slot.kind === 'image_url' ? 'url' : undefined}
            value={text}
            maxLength={slot.max > 0 ? slot.max : undefined}
            placeholder={
              slot.kind === 'image_url'
                ? 'https://'
                : typeof slot.fallback === 'string'
                  ? slot.fallback
                  : ''
            }
            onChange={(e) => onChange(e.target.value)}
          />
          <Hint t={t} slot={slot} />
        </div>
      );

    case 'prose':
    case 'richtext':
      return (
        <div className="bld-field">
          <label htmlFor={id}>{label}</label>
          {/*
           * A content zone is a plain textarea, deliberately: a rich-text
           * widget would produce markup nobody sees and the server would take
           * half of it away. Typed HTML plus "here is what survived" is the
           * honest version (the old editor's rule, kept).
           */}
          <textarea
            id={id}
            className="bld-input"
            rows={slot.kind === 'richtext' ? 6 : 3}
            value={text}
            maxLength={slot.max > 0 ? slot.max : undefined}
            onChange={(e) => onChange(e.target.value)}
          />
          <Hint t={t} slot={slot} />
          {slot.kind === 'richtext' && props.zone !== undefined ? (
            <Survived zone={props.zone} html={text} />
          ) : null}
        </div>
      );

    case 'enum': {
      const options = slot.options ?? [];
      const fallback = typeof slot.fallback === 'string' ? slot.fallback : '';
      // A short closed set reads best as a row of choices; a long one as a select.
      if (options.length > 0 && options.length <= 5) {
        const current = text === '' ? fallback : text;
        return (
          <div className="bld-field">
            <span id={id}>{label}</span>
            <div className="bld-choice" role="group" aria-labelledby={id}>
              {options.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={current === option}
                  onClick={() => onChange(option === fallback ? undefined : option)}
                >
                  {optionLabel(t, option)}
                </button>
              ))}
            </div>
            <Hint t={t} slot={slot} />
          </div>
        );
      }
      return (
        <div className="bld-field">
          <label htmlFor={id}>{label}</label>
          <select
            id={id}
            className="bld-input"
            value={text}
            onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
          >
            <option value="">
              {fallback === ''
                ? t('builder.slot.default')
                : t('builder.slot.defaultIs', { value: optionLabel(t, fallback) })}
            </option>
            {options.map((option) => (
              <option key={option} value={option}>
                {optionLabel(t, option)}
              </option>
            ))}
          </select>
          <Hint t={t} slot={slot} />
        </div>
      );
    }

    case 'flag':
      return (
        <label className="bld-check">
          <input
            type="checkbox"
            checked={typeof value === 'boolean' ? value : slot.fallback !== false}
            onChange={(e) => onChange(e.target.checked)}
          />
          {label}
        </label>
      );

    case 'number':
      return (
        <div className="bld-field">
          <label htmlFor={id}>{label}</label>
          <input
            id={id}
            className="bld-input"
            type="number"
            min={slot.min}
            max={slot.max > 0 ? slot.max : undefined}
            value={typeof value === 'number' ? String(value) : ''}
            placeholder={typeof slot.fallback === 'number' ? String(slot.fallback) : ''}
            onChange={(e) =>
              onChange(e.target.value === '' ? undefined : Math.round(Number(e.target.value)))
            }
          />
          <Hint t={t} slot={slot} />
        </div>
      );

    case 'rows':
      return props.policyRules === undefined ? (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={ROW} />
      ) : (
        <PolicyRows
          slot={slot}
          label={label}
          value={value}
          rules={props.policyRules}
          onChange={onChange}
        />
      );
    case 'images':
      return (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={IMAGE} />
      );
    case 'qa':
      return <ListField slot={slot} label={label} value={value} onChange={onChange} fields={QA} />;
    case 'links':
      return (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={LINK} />
      );
    case 'features':
      return (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={FEATURE} />
      );
    case 'panels':
      return (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={PANEL} />
      );
    case 'tiers':
      return (
        <ListField slot={slot} label={label} value={value} onChange={onChange} fields={TIER} />
      );
    case 'strings':
      return <StringsField slot={slot} label={label} value={value} onChange={onChange} />;
  }
}

/* ------------------------------------------------------------------ */
/* What survived the sanitizer                                         */
/* ------------------------------------------------------------------ */

/**
 * What the server kept, shown as **bytes** — the textarea above already holds
 * the stored markup (the document is re-read from the publish answer), so this
 * names what went. The console owns no `dangerouslySetInnerHTML`; the site's
 * `RichText` is the one place a content zone is rendered.
 */
function Survived({ zone, html }: { zone: ZoneReport; html: string }): ReactNode {
  const t = useT();
  if (!zone.changed) return <span className="ok">{t('branding.survivedUnchanged')}</span>;
  return (
    <div className="bld-survived">
      <p className="warn">
        {t('branding.survived', {
          removed: zone.removed.join(', '),
          before: zone.before_bytes,
          after: zone.after_bytes,
        })}
      </p>
      <pre>{html}</pre>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lists                                                               */
/* ------------------------------------------------------------------ */

type Entry = Record<string, unknown>;

interface FieldDef {
  key: string;
  kind: 'text' | 'long' | 'url' | 'select' | 'lines' | 'check';
  options?: readonly string[];
}

const ROW: FieldDef[] = [
  { key: 'label', kind: 'text' },
  { key: 'value', kind: 'text' },
];
const IMAGE: FieldDef[] = [
  { key: 'src', kind: 'url' },
  { key: 'alt', kind: 'text' },
  { key: 'caption', kind: 'text' },
];
const QA: FieldDef[] = [
  { key: 'q', kind: 'text' },
  { key: 'a', kind: 'long' },
];
const LINK: FieldDef[] = [
  { key: 'label', kind: 'text' },
  { key: 'url', kind: 'url' },
];
const FEATURE: FieldDef[] = [
  { key: 'icon', kind: 'select', options: FEATURE_ICONS },
  { key: 'title', kind: 'text' },
  { key: 'text', kind: 'long' },
  { key: 'label', kind: 'text' },
  { key: 'image', kind: 'url' },
];
const PANEL: FieldDef[] = [
  { key: 'title', kind: 'text' },
  { key: 'text', kind: 'long' },
  { key: 'image', kind: 'url' },
  { key: 'bullets', kind: 'lines' },
  { key: 'link_label', kind: 'text' },
  { key: 'link', kind: 'text' },
];
const TIER: FieldDef[] = [
  { key: 'name', kind: 'text' },
  { key: 'price', kind: 'text' },
  { key: 'period', kind: 'text' },
  { key: 'note', kind: 'long' },
  { key: 'bullets', kind: 'lines' },
  { key: 'link_label', kind: 'text' },
  { key: 'link_target', kind: 'text' },
  { key: 'featured', kind: 'check' },
];

function entriesOf(value: unknown): Entry[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) =>
    entry !== null && typeof entry === 'object' && !Array.isArray(entry) ? (entry as Entry) : {},
  );
}

function fieldName(t: T, key: string): string {
  const own = t('builder.fieldName', { field: key });
  return own === '' ? key : own;
}

/**
 * **One list editor over a field table.** Add, remove, move up and down; each
 * entry is a small card of inputs. A field left empty is removed from the
 * entry rather than sent as `''`, and a key the table does not draw is kept.
 */
function ListField({
  slot,
  label,
  value,
  fields,
  onChange,
}: {
  slot: SlotSpec;
  label: string;
  value: unknown;
  fields: readonly FieldDef[];
  onChange: (value: unknown) => void;
}): ReactNode {
  const t = useT();
  const entries = entriesOf(value);
  const set = (index: number, key: string, next: unknown): void => {
    const copy = entries.map((e) => ({ ...e }));
    const entry = copy[index];
    if (entry === undefined) return;
    if (
      next === '' ||
      next === undefined ||
      next === false ||
      (Array.isArray(next) && next.length === 0)
    ) {
      delete entry[key];
    } else {
      entry[key] = next;
    }
    onChange(copy);
  };
  const move = (index: number, delta: number): void => {
    const copy = [...entries];
    const a = copy[index];
    const b = copy[index + delta];
    if (a === undefined || b === undefined) return;
    copy[index] = b;
    copy[index + delta] = a;
    onChange(copy);
  };
  const blank = (): Entry => {
    // The two-key shapes the server requires both halves of start with both.
    const out: Entry = {};
    for (const field of fields)
      if (field.kind !== 'check' && field.kind !== 'lines') out[field.key] = '';
    return out;
  };

  return (
    <fieldset className="bld-list">
      <legend>{label}</legend>
      {entries.length === 0 ? <p className="bld-hint">{t('builder.list.empty')}</p> : null}
      <ol>
        {entries.map((entry, index) => {
          const n = index + 1;
          return (
            <li key={index} className="bld-list__entry">
              {fields.map((field) => (
                <EntryField
                  key={field.key}
                  field={field}
                  label={`${fieldName(t, field.key)} ${String(n)}`}
                  shown={fieldName(t, field.key)}
                  value={entry[field.key]}
                  onChange={(next) => set(index, field.key, next)}
                />
              ))}
              <div className="bld-list__tools">
                <button
                  type="button"
                  className="linkish"
                  aria-label={t('builder.list.up', { label, n })}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="linkish"
                  aria-label={t('builder.list.down', { label, n })}
                  disabled={index === entries.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="linkish"
                  aria-label={t('builder.list.remove', { label, n })}
                  onClick={() => onChange(entries.filter((_, i) => i !== index))}
                >
                  {t('common.remove')}
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="button button--sm"
        disabled={slot.max > 0 && entries.length >= slot.max}
        onClick={() => onChange([...entries, blank()])}
      >
        {t('builder.list.add', { label })}
      </button>
    </fieldset>
  );
}

function EntryField({
  field,
  label,
  shown,
  value,
  onChange,
}: {
  field: FieldDef;
  /** The accessible name — the field and its entry number. */
  label: string;
  /** The visible name — the field alone; the card says which entry. */
  shown: string;
  value: unknown;
  onChange: (value: unknown) => void;
}): ReactNode {
  const t = useT();
  const id = useId();
  const text = typeof value === 'string' ? value : '';
  switch (field.kind) {
    case 'check':
      return (
        <label className="bld-check">
          <input
            type="checkbox"
            aria-label={label}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          {shown}
        </label>
      );
    case 'select':
      return (
        <div className="bld-field">
          <label htmlFor={id}>{shown}</label>
          <select
            id={id}
            aria-label={label}
            className="bld-input"
            value={text}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{t('builder.slot.none')}</option>
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {optionLabel(t, option)}
              </option>
            ))}
          </select>
        </div>
      );
    case 'lines': {
      const list = Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
      return (
        <div className="bld-field">
          <label htmlFor={id}>{shown}</label>
          <textarea
            id={id}
            aria-label={label}
            className="bld-input"
            rows={3}
            value={list.join('\n')}
            onChange={(e) =>
              onChange(
                e.target.value
                  .split('\n')
                  .map((line) => line.trimEnd())
                  .filter((line, i, all) => line !== '' || i < all.length - 1),
              )
            }
          />
          <span className="bld-hint">{t('builder.list.oneALine')}</span>
        </div>
      );
    }
    case 'long':
      return (
        <div className="bld-field">
          <label htmlFor={id}>{shown}</label>
          <textarea
            id={id}
            aria-label={label}
            className="bld-input"
            rows={2}
            value={text}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    default:
      return (
        <div className="bld-field">
          <label htmlFor={id}>{shown}</label>
          <input
            id={id}
            aria-label={label}
            className="bld-input"
            type={field.kind === 'url' ? 'url' : 'text'}
            inputMode={field.kind === 'url' ? 'url' : undefined}
            placeholder={field.kind === 'url' ? 'https://' : ''}
            value={text}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
  }
}

/** A plain list of short strings — the inquiry's occasions, the booking band's services. */
function StringsField({
  slot,
  label,
  value,
  onChange,
}: {
  slot: SlotSpec;
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
}): ReactNode {
  const t = useT();
  const entries = Array.isArray(value) ? value.map((v) => (typeof v === 'string' ? v : '')) : [];
  return (
    <fieldset className="bld-list">
      <legend>{label}</legend>
      <ol>
        {entries.map((entry, index) => (
          <li key={index} className="bld-list__row">
            <input
              className="bld-input"
              aria-label={t('branding.stringLabel', { label, n: index + 1 })}
              value={entry}
              maxLength={slot.entry_max}
              onChange={(e) => {
                const copy = [...entries];
                copy[index] = e.target.value;
                onChange(copy);
              }}
            />
            <button
              type="button"
              className="linkish"
              aria-label={t('builder.list.remove', { label, n: index + 1 })}
              onClick={() => onChange(entries.filter((_, i) => i !== index))}
            >
              {t('common.remove')}
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        className="button button--sm"
        disabled={slot.max > 0 && entries.length >= slot.max}
        onClick={() => onChange([...entries, ''])}
      >
        {t('builder.list.add', { label })}
      </button>
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* The policies block's rows                                           */
/* ------------------------------------------------------------------ */

/**
 * The kinds an operator may tag a `policies` row with. `house_gap` and
 * `waiver` are not here: the engine derives both and the owner has no
 * sentence of their own to tag about them.
 */
const POLICY_KINDS = [
  '',
  'cancellation',
  'lead_time',
  'request_hold',
  'pickup_expiry',
  'deposit',
  'other',
] as const;

function readRows(value: unknown): AuthoredPolicy[] {
  return entriesOf(value).map((row) => ({
    label: typeof row['label'] === 'string' ? row['label'] : '',
    value: typeof row['value'] === 'string' ? row['value'] : '',
    ...(typeof row['kind'] === 'string' ? { kind: row['kind'] as AuthoredPolicy['kind'] } : {}),
  }));
}

/**
 * `{label, value}` rows **and the badge that says when the engine has the
 * last word** (GS2) — ported whole from the old editor: a row tagged with a
 * kind the engine derives for this business is not published, the engine's
 * row is, and the badge quotes the rule shown instead; an untagged row that
 * merely *looks* like a rule gets the softer hint.
 */
function PolicyRows({
  slot,
  label,
  value,
  rules,
  onChange,
}: {
  slot: SlotSpec;
  label: string;
  value: unknown;
  rules: readonly DerivedPolicy[];
  onChange: (value: unknown) => void;
}): ReactNode {
  const t = useT();
  const rows = readRows(value);
  const patch = (index: number, next: AuthoredPolicy): void => {
    const copy = [...rows];
    copy[index] = next;
    onChange(copy);
  };
  return (
    <fieldset className="bld-list">
      <legend>{label}</legend>
      <ol>
        {rows.map((row, index) => {
          const n = index + 1;
          const overriding = rules.find((rule) => rule.kind === row.kind);
          const suspected = row.kind === undefined ? suspectedPolicyKinds(row, rules) : [];
          return (
            <li key={index} className="bld-list__entry">
              <input
                className="bld-input"
                aria-label={t('branding.rowLabel', { label, n })}
                value={row.label}
                onChange={(e) => patch(index, { ...row, label: e.target.value })}
              />
              <input
                className="bld-input"
                aria-label={t('branding.rowValue', { label, n })}
                value={row.value}
                onChange={(e) => patch(index, { ...row, value: e.target.value })}
              />
              <select
                className="bld-input"
                aria-label={t('branding.rowKind', { label, n })}
                value={row.kind ?? ''}
                onChange={(e) => {
                  const rest: AuthoredPolicy = { label: row.label, value: row.value };
                  patch(
                    index,
                    e.target.value === ''
                      ? rest
                      : { ...rest, kind: e.target.value as AuthoredPolicy['kind'] },
                  );
                }}
              >
                {POLICY_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`branding.policyKind.${kind === '' ? 'none' : kind}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="linkish"
                aria-label={t('builder.list.remove', { label, n })}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              >
                {t('common.remove')}
              </button>
              {overriding === undefined ? null : (
                <span className="flag flag--bad" role="status">
                  {t('branding.policyOverridden', { rule: policyRuleWords(overriding, t) })}
                </span>
              )}
              {overriding !== undefined || suspected.length === 0 ? null : (
                <span className="flag" role="status">
                  {t('branding.policySuspected', {
                    rules: suspected
                      .map((kind) => rules.find((rule) => rule.kind === kind))
                      .filter((rule): rule is DerivedPolicy => rule !== undefined)
                      .map((rule) => policyRuleWords(rule, t))
                      .join('; '),
                  })}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="button button--sm"
        disabled={slot.max > 0 && rows.length >= slot.max}
        onClick={() => onChange([...rows, { label: '', value: '' }])}
      >
        {t('branding.addRow')}
      </button>
    </fieldset>
  );
}

/** The console's own words for an engine rule. */
function policyRuleWords(rule: DerivedPolicy, t: T): string {
  switch (rule.kind) {
    case 'lead_time':
      return [
        rule.minutes > 0 ? t('branding.policyRule.lead_time', { minutes: rule.minutes }) : '',
        rule.same_day_cutoff === undefined
          ? ''
          : t('branding.policyRule.same_day_cutoff', { time: rule.same_day_cutoff }),
      ]
        .filter((part) => part !== '')
        .join(' ');
    case 'request_hold':
      return t('branding.policyRule.request_hold', { hours: rule.hours });
    case 'pickup_expiry':
      return t('branding.policyRule.pickup_expiry', { days: rule.days });
    case 'deposit':
      return rule.mode === 'fixed'
        ? t('branding.policyRule.deposit_fixed', { cents: rule.fixed_cents ?? 0 })
        : t('branding.policyRule.deposit_percent', {
            percent: rule.percent ?? 0,
            minCents: rule.min_cents,
          });
    case 'house_gap':
      return t('branding.policyRule.house_gap', {
        minutes: Math.max(rule.before_min, rule.after_min),
      });
    case 'waiver':
      return t('branding.policyRule.waiver', { title: rule.title });
  }
}
